import React from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import CareTeamMap from "../components/CareTeamMap";
import TeamPortraitGroup from "../components/TeamPortraitGroup";
import TrustpilotInline from "../components/TrustpilotInline";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";
import { SINGLE_TIER_ONE } from "../constants/pricing";
import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const processSteps = [
	[
		"01",
		"Talk to a Family Care Advisor",
		"Tell us what is changing, what matters at home and when support may be needed.",
		[
			{
				src: `${imagePath}/team/matthew.png`,
				alt: "Matthew, an Edyn Family Care Advisor",
			},
		],
		{
			label: "Arrange a call",
			href: "/care-consultation/",
		},
	],
	[
		"02",
		"Complete a care assessment",
		"A Care Manager gets to know the person, their routines, needs and home environment.",
		[
			{
				src: `${imagePath}/team/orianne.png`,
				alt: "Orianne, an Edyn Care Manager",
			},
		],
	],
	[
		"03",
		"Meet carefully selected carers",
		"Review suitable professional carers and speak with the people you would like to meet.",
		[
			{
				src: `${imagePath}/olivia.png`,
				alt: "Olivia, an Edyn live-in carer",
			},
		],
	],
	[
		"04",
		"Begin care with ongoing support",
		"Your care team stays involved, reviews the plan and helps when circumstances change.",
		[
			{
				src: `${imagePath}/olivia.png`,
				alt: "Olivia, an Edyn live-in carer",
			},
			{
				src: `${imagePath}/team/orianne.png`,
				alt: "Orianne, an Edyn Care Manager",
			},
			{
				src: `${imagePath}/team/sarah.png`,
				alt: "Sarah, an Edyn Care Coordinator",
			},
		],
	],
];

const careNeeds = [
	["Companionship and day-to-day support", "/companionship-care/"],
	["Couples care", "/couples-care/"],
	["Dementia", "/condition/dementia/"],
	["Parkinson's", "/condition/parkinsons/"],
	["Stroke recovery", "/condition/stroke/"],
	["Cancer care", "/condition/cancer/"],
	["Multiple sclerosis", "/condition/multiple-sclerosis/"],
];

const carerStandards = [
	"Enhanced DBS checked",
	"Three references checked",
	"First-class training",
	"Excellent spoken and written English",
	"Rigorous safe recruitment checks",
	"Ongoing quality oversight",
];

const adviceLinks = [
	[
		"When is care needed?",
		"/blog/when-is-care-needed-how-to-spot-the-signs-and-what-to-do-next/",
		"Starting the conversation",
	],
	[
		"Live-in care vs a care home",
		"/blog/live-in-care-vs-care-home-which-option-is-right/",
		"Comparing care",
	],
	[
		"What is the cost of live-in care?",
		"/blog/what-is-the-cost-of-live-in-care-2/",
		"Planning costs",
	],
];

const proofStats = [
	["500+", "families helped"],
	["4.8 \u2605", "on Trustpilot", "4.8 stars on Trustpilot"],
	["4.5 \u2605", "on Google", "4.5 stars on Google"],
	["11m", "care hours delivered"],
	["2.7m", "cups of tea shared"],
];

const ProofRibbonGroup = ({ duplicate = false }) => (
	<div
		className="home-proof-ribbon__group"
		aria-hidden={duplicate ? "true" : undefined}
	>
		{proofStats.map(([value, label, ariaLabel]) => (
			<p className="home-proof-ribbon__stat" key={`${value}-${label}`}>
				<strong aria-label={ariaLabel}>{value}</strong>
				<span>{label}</span>
			</p>
		))}
		<div className="home-proof-ribbon__press">
			<p>As seen in</p>
			<div>
				<img
					src={`${imagePath}/thegaurdian.png`}
					alt={duplicate ? "" : "The Guardian"}
				/>
				<img
					src={`${imagePath}/thetimes.png`}
					alt={duplicate ? "" : "The Times"}
				/>
				<img
					src={`${imagePath}/theeveningstandard.png`}
					alt={duplicate ? "" : "Evening Standard"}
				/>
			</div>
		</div>
	</div>
);

export default function Home() {
	const { heroVideo } = useStaticQuery(graphql`
		query RedesignedHomeHeroVideo {
			heroVideo: file(relativePath: { eq: "Landingsection4.mp4" }) {
				childVideoFfmpeg {
					mp4: transcode(
						maxWidth: 1600
						maxHeight: 900
						fileExtension: "mp4"
						codec: "libx264"
					) {
						src
					}
				}
			}
		}
	`);
	const heroVideoSrc = heroVideo.childVideoFfmpeg.mp4.src;

	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Live-in Care at Home | Edyn Care"
				description="Fully managed, CQC-regulated live-in care with carefully selected professional carers and a dedicated care team supporting every placement."
				path="/"
			>
				<meta
					name="google-site-verification"
					content="-0ijp0qAzzNoyx7Hq9kOIgChiO_1Y2A8VXbyU-gTA3I"
				/>
			</Seo>
			<Header />

			<main id="main-content" className="edyn-home">
				<section className="home-hero">
					<div className="home-hero__media" aria-hidden="true">
						<video
							autoPlay
							loop
							muted
							playsInline
							preload="metadata"
							poster={`${imagePath}/header-img.jpg`}
						>
							<source src={heroVideoSrc} type="video/mp4" />
						</video>
					</div>
					<div className="site-shell home-hero__content">
						<div className="home-hero__copy">
							<p className="eyebrow">Fully managed live-in care</p>
							<h1>Live-in care that keeps life feeling like yours.</h1>
							<p className="home-hero__lead">
								A professional carer in the home, supported by an experienced
								care team. Edyn makes arranging one-to-one care feel clearer
								from the first conversation onwards.
							</p>
							<div className="button-row">
								<Link
									className="button button--primary"
									to="/care-consultation/"
								>
									Arrange a call
								</Link>
								<Link className="button button--text" to="/liveincare/">
									Explore live-in care <span aria-hidden="true">&#8594;</span>
								</Link>
							</div>
							<TrustpilotInline />
						</div>
						<div className="home-hero__note">
							<span aria-hidden="true">&#10003;</span>
							<p>
								<strong>One-to-one care.</strong>
								<br />A whole team behind it.
							</p>
						</div>
					</div>
				</section>

				<section
					className="home-proof-ribbon"
					aria-label="Edyn Care in numbers and the press"
				>
					<div className="home-proof-ribbon__viewport">
						<div className="home-proof-ribbon__track">
							<ProofRibbonGroup />
							<ProofRibbonGroup duplicate />
						</div>
					</div>
				</section>

				<section className="home-life section-space">
					<div className="site-shell home-life__grid">
						<div className="home-life__story">
							<p className="eyebrow">Care around a life</p>
							<h2>
								Home is more than a place. It is the shape of everyday life.
							</h2>
							<figure className="home-life__visual">
								<img
									src="/assets/marketing-site/images/livein-imageclus-06.png"
									alt="A dog resting comfortably beside a sofa at home"
									loading="lazy"
								/>
								<figcaption>
									<span aria-hidden="true">&#10003;</span>
									Care that fits around home.
								</figcaption>
							</figure>
						</div>
						<div className="home-life__content">
							<p className="section-lead">
								Live-in care helps someone stay close to familiar routines,
								people, pets and places, while receiving professional support
								that adapts as their needs change.
							</p>
							<ul className="home-life__list">
								<li>
									<span>01</span>
									<p>
										<strong>Keep familiar routines</strong>Meals, mornings and
										meaningful rituals can continue.
									</p>
								</li>
								<li>
									<span>02</span>
									<p>
										<strong>Receive one-to-one support</strong>Care is shaped
										around one person, not a shift pattern.
									</p>
								</li>
								<li>
									<span>03</span>
									<p>
										<strong>Stay connected</strong>Family, friends and community
										remain part of daily life.
									</p>
								</li>
								<li>
									<span>04</span>
									<p>
										<strong>Adapt with confidence</strong>The care plan and team
										respond as circumstances change.
									</p>
								</li>
							</ul>
						</div>
					</div>
				</section>

				<section className="home-team section-space section-dark">
					<div className="site-shell home-team__grid">
						<div className="home-team__copy">
							<p className="eyebrow eyebrow--light">Fully managed care</p>
							<h2>
								One professional carer in the home. A complete care team behind
								them.
							</h2>
							<p>
								Edyn is not simply an introductory agency. Your professional
								carer is supported by people who understand the care plan,
								coordinate the placement and stay available to the family.
							</p>
							<Link className="button button--cream" to="/approach/">
								See how Edyn works
							</Link>
						</div>
						<CareTeamMap />
					</div>
				</section>

				<section className="home-process section-space" id="how-care-starts">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">A supported start</p>
								<h2>We make arranging care feel clearer.</h2>
							</div>
							<p>
								You can begin with a conversation. We will explain each next
								step and help your family make a considered decision.
							</p>
						</div>
						<ol className="home-process__steps">
							{processSteps.map(([number, title, description, people, cta]) => (
								<li key={number}>
									<div className="home-process__step-head">
										<span>{number}</span>
										<div
											className={`home-process__portraits${
												people.length > 1
													? " home-process__portraits--group"
													: ""
											}`}
										>
											{people.map((person) => (
												<img
													key={person.src}
													src={person.src}
													alt={person.alt}
													loading="lazy"
												/>
											))}
										</div>
									</div>
									<h3>{title}</h3>
									<p>{description}</p>
									{cta && (
										<Link className="home-process__cta" to={cta.href}>
											<span
												className="home-process__cta-status"
												aria-hidden="true"
											/>
											<span>{cta.label}</span>
											<span aria-hidden="true">→</span>
										</Link>
									)}
								</li>
							))}
						</ol>
						<div className="home-process__reviews">
							<p>
								Families can read independent feedback on the care team
								supporting them.
							</p>
							<MicroCombo
								classNames="home-process__trustpilot home-process__trustpilot--desktop"
								alignment="right"
								width="270px"
							/>
							<MicroCombo
								classNames="home-process__trustpilot home-process__trustpilot--mobile"
								alignment="left"
								width="270px"
							/>
						</div>
					</div>
				</section>

				<section className="home-difference section-space">
					<div className="site-shell">
						<div className="section-heading">
							<p className="eyebrow">Why families choose Edyn</p>
							<h2>Dependable care is built, not just matched.</h2>
						</div>
						<div className="home-difference__grid">
							<article>
								<p className="home-difference__number">01</p>
								<h3>Carefully selected professional carers</h3>
								<p>
									Edyn recruits, vets and prepares carers before introducing
									people whose skills and approach suit the care plan and
									household.
								</p>
								<Link to="/our-carers/">
									Meet our carers <span aria-hidden="true">&#8594;</span>
								</Link>
							</article>
							<article className="home-difference__feature">
								<TeamPortraitGroup className="home-difference__team" />
								<p className="home-difference__number">02</p>
								<h3>Support that continues after care begins</h3>
								<p>
									A Care Manager and coordination team remain involved, helping
									with reviews, changes and continuity.
								</p>
							</article>
							<article>
								<p className="home-difference__number">03</p>
								<h3>Family visibility without surveillance</h3>
								<p>
									Edyn's digital experience helps families read the care plan,
									follow daily notes and stay connected to the team.
								</p>
								<Link to="/approach/">
									Explore family visibility{" "}
									<span aria-hidden="true">&#8594;</span>
								</Link>
							</article>
						</div>
					</div>
				</section>

				<section
					className="home-standards"
					aria-labelledby="home-standards-title"
				>
					<div className="home-standards__main">
						<div className="site-shell home-standards__inner">
							<div className="home-standards__visual">
							<picture>
								<source srcSet={`${imagePath}/livein-imageclus-09-640.webp 640w, ${imagePath}/livein-imageclus-09-1200.webp 1200w`} sizes="(max-width: 700px) 100vw, 50vw" type="image/webp" />
								<img src={`${imagePath}/livein-imageclus-09-1200.webp`} alt="A professional carer sharing a relaxed moment with an older woman at home" width="1200" height="1836" loading="lazy" />
							</picture>
								<div className="home-standards__stat">
									<strong>~2%</strong>
									<span>of applicants join Edyn's carer community</span>
								</div>
							</div>
							<div className="home-standards__story">
								<p className="eyebrow">Professional carers</p>
								<h2 id="home-standards-title">
									The right person makes all the difference.
								</h2>
								<p className="home-standards__lead">
									We look beyond experience alone. Every Edyn carer is carefully
									checked, trained and selected for the qualities that help care
									feel capable, calm and personal.
								</p>
								<ul className="home-standards__checks">
									{carerStandards.map((standard) => (
										<li key={standard}>
											<span aria-hidden="true">&#10003;</span>
											{standard}
										</li>
									))}
								</ul>
								<p className="home-standards__regulated">
									CQC-regulated, fully managed care in England.
								</p>
								<Link className="button button--primary" to="/our-carers/">
									How we select and support carers
								</Link>
							</div>
						</div>
					</div>
				</section>

				<section
					id="thoughtful-technology"
					className="home-technology section-space section-soft"
				>
					<img
						className="home-technology__texture home-technology__texture--top"
						src={`${imagePath}/group-2772.png`}
						alt=""
						aria-hidden="true"
					/>
					<img
						className="home-technology__texture home-technology__texture--bottom"
						src={`${imagePath}/group-2772.png`}
						alt=""
						aria-hidden="true"
					/>
					<div className="site-shell home-technology__grid">
						<div className="home-technology__copy">
							<p className="eyebrow">Thoughtful technology</p>
							<h2>Stay close to the care being delivered.</h2>
							<p>
								Technology should make care more transparent and human, not
								become a barrier. Edyn gives families a clear way to follow the
								plan and stay informed.
							</p>
							<ul className="home-technology__features">
								<li>Read the personalised care plan</li>
								<li>View daily carer notes</li>
								<li>Stay in contact with the care team</li>
								<li>Follow relevant care and wellbeing information</li>
							</ul>
							<Link className="button button--outline" to="/approach/">
								How family visibility works
							</Link>
						</div>
						<div className="home-technology__visual">
							<img
								className="home-technology__photo"
								src={`${imagePath}/tech-image-01.png`}
								alt="An older woman at home beside Edyn's digital visit summary"
								loading="lazy"
							/>
							<div className="home-technology__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Care, clearly shared.</strong>
									<small>Plans, notes and updates in one place.</small>
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="home-needs section-space">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Care needs and conditions</p>
								<h2>A person is never just a diagnosis.</h2>
							</div>
							<p>
								Care is built around the individual's health, routines,
								preferences and goals. Explore how live-in support can respond
								to different needs.
							</p>
						</div>
						<div className="home-needs__links">
							{careNeeds.map(([label, href], index) => (
								<Link key={href} to={href}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									<strong>{label}</strong>
									<span aria-hidden="true">&#8599;</span>
								</Link>
							))}
						</div>
					</div>
				</section>

				<section className="home-pricing section-space section-clay">
					<div className="site-shell home-pricing__grid">
						<div>
							<p className="eyebrow">Transparent pricing</p>
							<h2>Understand the cost before you make a decision.</h2>
							<p>
								Live-in care pricing reflects the level of support required. A
								care assessment helps Edyn recommend the right tier and explain
								what is included.
							</p>
							<Link className="button button--primary" to="/pricing/">
								View all pricing and inclusions
							</Link>
						</div>
						<div className="home-pricing__figure">
							<p>Live-in care for one person</p>
							<strong>From {SINGLE_TIER_ONE}</strong>
							<span>per week</span>
							<p>
								Final pricing is confirmed after assessment. Additional costs
								and couple pricing are explained on the pricing page.
							</p>
						</div>
					</div>
				</section>

				<section className="home-locations section-space">
					<div className="site-shell home-locations__grid">
						<div className="home-locations__copy">
							<p className="eyebrow">Care across England</p>
							<h2>Local understanding, supported by one Edyn team.</h2>
							<p>
								Explore current service areas and speak with the team about
								arranging care where your family lives.
							</p>
							<div className="home-locations__list">
								<Link to="/location/london/">London</Link>
								<Link to="/location/south-east/">South East</Link>
								<Link to="/location/south-west/">South West</Link>
								<Link to="/location/east-midlands/">East Midlands</Link>
								<Link to="/location/west-midlands/">West Midlands</Link>
								<Link to="/location/west-of-england/">West of England</Link>
							</div>
						</div>
						<div
							className="home-locations__visual"
							role="img"
							aria-label="Edyn provides live-in care through six regional teams across England"
						>
							<div className="home-locations__map" aria-hidden="true">
								<span className="home-locations__map-shape" />
							</div>
							<div className="home-locations__map-label">
								<span className="home-locations__map-count">6</span>
								<span>
									<strong>Regional care teams</strong>
									<small>One Edyn standard across England</small>
								</span>
							</div>
						</div>
					</div>
				</section>

				<section className="home-advice section-space section-soft">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Planning and advice</p>
								<h2>Clear guidance for decisions that can feel complicated.</h2>
							</div>
							<Link className="text-link" to="/blog/">
								Browse all advice <span aria-hidden="true">&#8594;</span>
							</Link>
						</div>
						<div className="home-advice__grid">
							{adviceLinks.map(([title, href, category], index) => (
								<article key={href}>
									<p>{category}</p>
									<span>{String(index + 1).padStart(2, "0")}</span>
									<h3>
										<Link to={href}>{title}</Link>
									</h3>
									<Link className="text-link" to={href}>
										Read guidance <span aria-hidden="true">&#8594;</span>
									</Link>
								</article>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
